import { useEffect, useMemo, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  docSource,
  fetchDocumentBlob,
  listDocumentsForJob,
  typeChipColor,
  type DocumentSummary,
} from '../lib/documents';

// Worker bundled by Vite's `new URL(..., import.meta.url)` pattern.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

export function PDFViewer() {
  const { jobId, docId } = useParams<{ jobId: string; docId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const passedDoc = (location.state as { doc?: DocumentSummary } | null)?.doc ?? null;

  const [meta, setMeta] = useState<DocumentSummary | null>(passedDoc);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!docId) return;
    let cancelled = false;
    (async () => {
      try {
        const [b] = await Promise.all([
          fetchDocumentBlob(docId),
          (async () => {
            if (meta || !jobId) return;
            try {
              const all = await listDocumentsForJob(jobId);
              if (cancelled) return;
              const found = all.find((d) => d.id === docId) ?? null;
              setMeta(found);
            } catch {
              // metadata is best-effort; viewer still works without it
            }
          })(),
        ]);
        if (cancelled) return;
        setBlob(b);
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [docId, jobId, meta]);

  // Pass the Blob directly — react-pdf accepts Blob. Memoized so changing
  // local state (page, zoom) doesn't re-parse the PDF.
  const fileProp = useMemo(() => blob, [blob]);

  function openInDevice() {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-neutral-900 text-white">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 border-b border-white/10 bg-black/50 p-3">
        <button
          onClick={() => navigate(`/job/${jobId}/documents`)}
          className="rounded-full bg-white/10 px-3 py-1.5 text-sm"
        >
          ‹ Documents
        </button>
        <div className="min-w-0 flex-1 truncate text-center text-sm">
          {meta?.name ?? '…'}
        </div>
        <button
          onClick={openInDevice}
          disabled={!blob}
          className="rounded-full bg-white/15 px-3 py-1.5 text-xs disabled:opacity-40"
        >
          Open in Device
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 border-b border-white/10 bg-black/40 px-3 py-2 text-xs">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || !pageCount}
            className="rounded bg-white/10 px-2 py-1 disabled:opacity-30"
          >
            ‹
          </button>
          <span className="tabular-nums">
            {pageCount ? `${page} / ${pageCount}` : '—'}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(pageCount || p, p + 1))}
            disabled={!pageCount || page >= pageCount}
            className="rounded bg-white/10 px-2 py-1 disabled:opacity-30"
          >
            ›
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setZoom((z) => Math.max(MIN_ZOOM, z - ZOOM_STEP))}
            disabled={zoom <= MIN_ZOOM}
            className="rounded bg-white/10 px-2 py-1 disabled:opacity-30"
          >
            −
          </button>
          <span className="tabular-nums">{Math.round(zoom * 100)}%</span>
          <button
            onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP))}
            disabled={zoom >= MAX_ZOOM}
            className="rounded bg-white/10 px-2 py-1 disabled:opacity-30"
          >
            +
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div className="relative flex-1 overflow-auto">
        {err && (
          <div className="m-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
            Could not load document: {err}
          </div>
        )}
        {!err && !blob && (
          <div className="flex h-full items-center justify-center text-sm opacity-70">
            Loading PDF…
          </div>
        )}
        {!err && fileProp && (
          <div className="flex justify-center py-4">
            <Document
              file={fileProp}
              onLoadSuccess={({ numPages }) => {
                setPageCount(numPages);
                setPage((p) => Math.min(p, numPages) || 1);
              }}
              onLoadError={(e) => setErr(e.message)}
              loading={<div className="text-sm opacity-70">Rendering…</div>}
              error={<div className="text-sm text-red-300">PDF render failed.</div>}
            >
              <Page
                pageNumber={page}
                scale={zoom}
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
            </Document>
          </div>
        )}
      </div>

      {/* Footer meta */}
      {meta && (
        <div className="flex items-center gap-2 border-t border-white/10 bg-black/50 px-3 py-2 text-xs">
          <span
            className={`rounded-full ${typeChipColor(
              meta.type,
            )} px-2 py-0.5 text-[10px] font-bold uppercase text-white`}
          >
            {meta.type ?? 'Uncategorized'}
          </span>
          <span className="truncate opacity-80">{meta.name ?? '(untitled)'}</span>
          <span className="ml-auto opacity-60">{docSource(meta)}</span>
        </div>
      )}
    </div>
  );
}
